/**
 * @fileoverview SVG pie / donut chart
 * @module ctl-pie-chart
 * @version 1.0.0
 *
 * @description
 * Renders a responsive SVG pie or donut chart with a legend, labels,
 * and interactive tooltips. Supports segment toggling via the legend.
 *
 * @example
 * <ctl-pie-chart
 *   title="Traffic Sources"
 *   donut
 *   segments='[
 *     {"label":"Organic","value":42,"color":"#6200ee"},
 *     {"label":"Direct","value":28,"color":"#03dac6"},
 *     {"label":"Paid","value":18,"color":"#f59e0b"},
 *     {"label":"Social","value":12,"color":"#10b981"}
 *   ]'>
 * </ctl-pie-chart>
 */

import { safeJsonParse } from '../../../utils/safeJsonParse.js';
import "../foundation/ctl-skeleton.js";

const DEFAULT_COLORS = [
  "#6200ee", "#03dac6", "#f59e0b", "#10b981", "#ef4444",
  "#3b82f6", "#8b5cf6", "#ec4899", "#14b8a6", "#f97316"
];

const template = document.createElement("template");
template.innerHTML = `
<style>
  :host {
    display: block;
    font-family: var(--ctl-font-sans, system-ui, -apple-system, Segoe UI, Roboto, Arial);
    color: var(--ctl-color-text);
  }

  .card {
    background: var(--ctl-color-surface);
    border: 1px solid var(--ctl-color-border);
    border-radius: var(--ctl-radius-lg);
    box-shadow: var(--ctl-shadow-card);
    overflow: hidden;
  }

  .head {
    padding: var(--ctl-space-3-5) var(--ctl-space-4);
    border-bottom: 1px solid var(--ctl-color-border);
  }

  .title {
    font-size: var(--ctl-font-size-lg);
    font-weight: var(--ctl-font-weight-heavy);
    line-height: 1.2;
  }

  .body {
    display: flex;
    align-items: center;
    gap: var(--ctl-space-4);
    padding: var(--ctl-space-4);
    flex-wrap: wrap;
  }

  .chart-wrap {
    flex: 0 0 auto;
    position: relative;
  }

  svg {
    display: block;
    overflow: visible;
  }

  .slice {
    cursor: pointer;
    transition: opacity 120ms, transform 120ms;
    transform-origin: center;
  }

  .slice:hover, .slice:focus {
    opacity: 0.82;
    transform: scale(1.03);
    outline: none;
  }

  .donut-label {
    pointer-events: none;
    text-anchor: middle;
    dominant-baseline: middle;
  }

  .legend {
    flex: 1 1 140px;
    display: flex;
    flex-direction: column;
    gap: var(--ctl-space-2);
    min-width: 120px;
  }

  .legend-item {
    display: flex;
    align-items: center;
    gap: var(--ctl-space-2-5);
    cursor: pointer;
    user-select: none;
    padding: var(--ctl-space-1) 0;
  }

  .legend-swatch {
    width: 10px;
    height: 10px;
    border-radius: 2px;
    flex-shrink: 0;
  }

  .legend-label {
    font-size: var(--ctl-font-size-sm);
    color: var(--ctl-color-text);
    flex: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .legend-pct {
    font-size: var(--ctl-font-size-sm);
    font-weight: var(--ctl-font-weight-bold);
    color: var(--ctl-color-muted);
    flex-shrink: 0;
  }

  .tooltip {
    position: fixed;
    background: var(--ctl-color-surface-inverse);
    color: var(--ctl-color-on-surface-inverse);
    border-radius: var(--ctl-radius-md);
    padding: var(--ctl-space-2) var(--ctl-space-3);
    font-size: var(--ctl-font-size-sm);
    pointer-events: none;
    white-space: nowrap;
    box-shadow: var(--ctl-shadow-dropdown);
    opacity: 0;
    transition: opacity 100ms;
    z-index: 10000;
  }

  .tooltip.visible { opacity: 1; }

  :host([loading]) .real { display: none; }
  .loading { display: none; padding: var(--ctl-space-4); }
  :host([loading]) .loading { display: block; }
</style>

<div class="card" part="card">
  <div class="head" part="head">
    <div class="title" id="title"></div>
  </div>
  <div class="real">
    <div class="body" part="body">
      <div class="chart-wrap" part="chart-wrap">
        <svg id="svg" role="img" aria-label="Pie chart"></svg>
      </div>
      <div class="legend" id="legend" part="legend"></div>
    </div>
  </div>
  <div class="loading" part="loading" aria-hidden="true">
    <ctl-skeleton variant="rect" height="200px"></ctl-skeleton>
  </div>
</div>
<div class="tooltip" id="tooltip"></div>
`;

function polarToCartesian(cx, cy, r, angleDeg) {
  const rad = ((angleDeg - 90) * Math.PI) / 180;
  return { x: cx + r * Math.cos(rad), y: cy + r * Math.sin(rad) };
}

function describeArc(cx, cy, r, startAngle, endAngle) {
  const start = polarToCartesian(cx, cy, r, endAngle);
  const end = polarToCartesian(cx, cy, r, startAngle);
  const largeArc = endAngle - startAngle > 180 ? 1 : 0;
  return `M ${start.x.toFixed(3)} ${start.y.toFixed(3)} A ${r} ${r} 0 ${largeArc} 0 ${end.x.toFixed(3)} ${end.y.toFixed(3)}`;
}

export class CtlPieChart extends HTMLElement {
  static get observedAttributes() {
    return ["title", "segments", "donut", "loading", "size"];
  }

  constructor() {
    super();
    this.attachShadow({ mode: "open" }).appendChild(template.content.cloneNode(true));
    this._titleEl = this.shadowRoot.getElementById("title");
    this._legendEl = this.shadowRoot.getElementById("legend");
    this._svg = this.shadowRoot.getElementById("svg");
    this._tooltip = this.shadowRoot.getElementById("tooltip");
    this._segments = [];
    this._hidden = new Set();
  }

  connectedCallback() {
    this._parse();
    this._bindEvents();
    this._render();
  }

  /** Bind all interactive events once using delegation. */
  _bindEvents() {
    // Legend: toggle segment visibility
    this._legendEl.addEventListener("click", (e) => {
      const item = e.target.closest(".legend-item");
      if (!item) return;
      const idx = parseInt(item.dataset.idx, 10);
      if (Number.isNaN(idx)) return;
      if (this._hidden.has(idx)) this._hidden.delete(idx);
      else this._hidden.add(idx);
      this._render();
    });
    this._legendEl.addEventListener("keydown", (e) => {
      if (e.key !== "Enter" && e.key !== " ") return;
      const item = e.target.closest(".legend-item");
      if (!item) return;
      e.preventDefault();
      const idx = parseInt(item.dataset.idx, 10);
      if (Number.isNaN(idx)) return;
      if (this._hidden.has(idx)) this._hidden.delete(idx);
      else this._hidden.add(idx);
      this._render();
    });

    // Slices: tooltip via mouseover / mousemove / mouseout / focusin / focusout
    this._svg.addEventListener("mouseover", (e) => {
      const slice = e.target.closest(".slice");
      if (!slice) return;
      const from = e.relatedTarget instanceof Element ? e.relatedTarget.closest(".slice") : null;
      if (from === slice) return;
      const tip = this._tooltip;
      tip.innerHTML = `<strong>${this._esc(slice.dataset.label)}</strong>: ${this._esc(slice.dataset.value)} <span style="opacity:.7">(${this._esc(slice.dataset.pct)}%)</span>`;
      tip.classList.add("visible");
      tip.style.left = `${e.clientX + 12}px`;
      tip.style.top = `${e.clientY - 36}px`;
    });
    this._svg.addEventListener("mousemove", (e) => {
      const slice = e.target.closest(".slice");
      if (!slice) return;
      this._tooltip.style.left = `${e.clientX + 12}px`;
      this._tooltip.style.top = `${e.clientY - 36}px`;
    });
    this._svg.addEventListener("mouseout", (e) => {
      const slice = e.target.closest(".slice");
      if (!slice) return;
      const to = e.relatedTarget instanceof Element ? e.relatedTarget.closest(".slice") : null;
      if (to === slice) return;
      this._tooltip.classList.remove("visible");
    });
    this._svg.addEventListener("focusin", (e) => {
      const slice = e.target.closest(".slice");
      if (!slice) return;
      const tip = this._tooltip;
      tip.innerHTML = `<strong>${this._esc(slice.dataset.label)}</strong>: ${this._esc(slice.dataset.value)} (${this._esc(slice.dataset.pct)}%)`;
      const rect = slice.getBoundingClientRect();
      tip.style.left = `${rect.left + rect.width / 2}px`;
      tip.style.top = `${rect.top - 40}px`;
      tip.classList.add("visible");
    });
    this._svg.addEventListener("focusout", (e) => {
      const slice = e.target.closest(".slice");
      if (!slice) return;
      this._tooltip.classList.remove("visible");
    });
  }

  attributeChangedCallback(name) {
    if (name === "segments") this._parse();
    this._render();
  }

  get title() { return this.getAttribute("title") || ""; }
  set title(v) { this.setAttribute("title", String(v ?? "")); }

  get donut() { return this.hasAttribute("donut"); }
  set donut(v) { v ? this.setAttribute("donut", "") : this.removeAttribute("donut"); }

  get loading() { return this.hasAttribute("loading"); }
  set loading(v) { v ? this.setAttribute("loading", "") : this.removeAttribute("loading"); }

  get segments() { return this._segments; }
  set segments(v) {
    this._segments = Array.isArray(v) ? v : [];
    this.setAttribute("segments", JSON.stringify(this._segments));
  }

  _parse() {
    const raw = this.getAttribute("segments");
    if (raw) this._segments = safeJsonParse(raw, []);
  }

  _render() {
    this._titleEl.textContent = this.title;
    if (this.loading) return;

    const segs = this._segments.map((s, i) => ({
      ...s,
      color: s.color || DEFAULT_COLORS[i % DEFAULT_COLORS.length],
      visible: !this._hidden.has(i)
    }));

    this._renderLegend(segs);

    const size = parseInt(this.getAttribute("size") || "180", 10);
    const cx = size / 2;
    const cy = size / 2;
    const r = size / 2 - 4;
    const innerR = this.donut ? r * 0.55 : 0;

    const svg = this._svg;
    svg.setAttribute("viewBox", `0 0 ${size} ${size}`);
    svg.setAttribute("width", size);
    svg.setAttribute("height", size);
    svg.setAttribute("aria-label", `Pie chart: ${this.title}`);

    const visible = segs.filter(s => s.visible);
    const total = visible.reduce((sum, s) => sum + (Number(s.value) || 0), 0);
    if (total === 0 || visible.length === 0) { svg.innerHTML = ""; return; }

    let html = "";
    let angle = 0;

    visible.forEach((s, i) => {
      const pct = (Number(s.value) || 0) / total;
      const sweep = pct * 360;
      const startAngle = angle;
      const endAngle = angle + sweep;
      angle = endAngle;

      const path = describeArc(cx, cy, r, startAngle, endAngle - 0.01);
      const originalIdx = segs.findIndex(x => x.label === s.label && x.color === s.color);

      if (this.donut) {
        const innerPath = describeArc(cx, cy, innerR, startAngle, endAngle - 0.01);
        const startOuter = polarToCartesian(cx, cy, r, startAngle);
        const startInner = polarToCartesian(cx, cy, innerR, startAngle);
        const endOuter = polarToCartesian(cx, cy, r, endAngle - 0.01);
        const endInner = polarToCartesian(cx, cy, innerR, endAngle - 0.01);
        const largeArc = sweep > 180 ? 1 : 0;

        const d = [
          `M ${startOuter.x.toFixed(3)} ${startOuter.y.toFixed(3)}`,
          `A ${r} ${r} 0 ${largeArc} 1 ${endOuter.x.toFixed(3)} ${endOuter.y.toFixed(3)}`,
          `L ${endInner.x.toFixed(3)} ${endInner.y.toFixed(3)}`,
          `A ${innerR} ${innerR} 0 ${largeArc} 0 ${startInner.x.toFixed(3)} ${startInner.y.toFixed(3)}`,
          "Z"
        ].join(" ");

        html += `<path class="slice"
          d="${d}"
          fill="${s.color}"
          data-idx="${originalIdx}"
          data-label="${this._esc(s.label || "")}"
          data-value="${s.value}"
          data-pct="${(pct * 100).toFixed(1)}"
          tabindex="0"
          role="img"
          aria-label="${this._esc(s.label || "")}: ${(pct * 100).toFixed(1)}%"
        />`;
      } else {
        const startPt = polarToCartesian(cx, cy, r, startAngle);
        const endPt = polarToCartesian(cx, cy, r, endAngle - 0.01);
        const largeArc = sweep > 180 ? 1 : 0;
        const d = `M ${cx} ${cy} L ${startPt.x.toFixed(3)} ${startPt.y.toFixed(3)} A ${r} ${r} 0 ${largeArc} 1 ${endPt.x.toFixed(3)} ${endPt.y.toFixed(3)} Z`;
        html += `<path class="slice"
          d="${d}"
          fill="${s.color}"
          data-idx="${originalIdx}"
          data-label="${this._esc(s.label || "")}"
          data-value="${s.value}"
          data-pct="${(pct * 100).toFixed(1)}"
          tabindex="0"
          role="img"
          aria-label="${this._esc(s.label || "")}: ${(pct * 100).toFixed(1)}%"
        />`;
      }
    });

    // Donut center label (total)
    if (this.donut && total > 0) {
      html += `
        <text class="donut-label" x="${cx}" y="${cy - 8}" fill="var(--ctl-color-text)" font-size="18" font-weight="800">${this._formatNum(total)}</text>
        <text class="donut-label" x="${cx}" y="${cy + 12}" fill="var(--ctl-color-muted)" font-size="11">Total</text>
      `;
    }

    svg.innerHTML = html;
  }

  /** Update legend HTML only; event handling is delegated via _bindEvents(). */
  _renderLegend(segs) {
    const total = segs.filter(s => s.visible).reduce((sum, s) => sum + (Number(s.value) || 0), 0);
    this._legendEl.innerHTML = segs.map((s, i) => {
      const pct = total > 0 ? ((Number(s.value) || 0) / total * 100).toFixed(1) : "0";
      return `
        <div class="legend-item" data-idx="${i}" role="checkbox" aria-checked="${s.visible}" tabindex="0">
          <div class="legend-swatch" style="background:${s.color};opacity:${s.visible ? 1 : 0.3};"></div>
          <span class="legend-label" style="opacity:${s.visible ? 1 : 0.4}">${this._esc(s.label || `Segment ${i + 1}`)}</span>
          <span class="legend-pct" style="opacity:${s.visible ? 1 : 0.4}">${s.visible ? pct + "%" : "–"}</span>
        </div>
      `;
    }).join("");
  }

  _formatNum(v) {
    if (v >= 1_000_000) return `${(v / 1_000_000).toFixed(1)}M`;
    if (v >= 1_000) return `${(v / 1_000).toFixed(1)}k`;
    return String(Math.round(v));
  }

  _esc(s) {
    return String(s ?? "")
      .replaceAll("&", "&amp;")
      .replaceAll("<", "&lt;")
      .replaceAll(">", "&gt;")
      .replaceAll('"', "&quot;");
  }
}

if (!customElements.get("ctl-pie-chart")) {
  customElements.define("ctl-pie-chart", CtlPieChart);
}
